import React, { useCallback, useEffect, useMemo } from 'react';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Typography } from '@mui/material';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import useForm from 'hooks/useForm';
import { getConditionalObject } from 'utils/data';
import { isPdfFormType } from 'utils/forms';

import { formsTestIds } from '../../forms.testIds';

import DynamicFieldBoldCheckbox from './DynamicFieldBoldCheckbox';
import {
  NotaryActionName,
  NotaryActor,
  notaryActorOptions,
  notaryOptionsSchema,
} from './DynamicFieldNotaryOptionsView.constants';
import { fullWidthCellStyles, sectionHeaderStyles } from './DynamicFieldOptions.styles';
import { findOptionByValue, getInitialHorizontalAlignment } from './DynamicFieldOptions.utils';
import DynamicFieldOptionsLayout from './DynamicFieldOptionsLayout';
import StyleViewOptions from './StyleViewOptions';

const DynamicFieldNotaryOptionsView = ({
  setParentView,
  chosenOption,
  handleClose,
  editor,
  onSave,
  action,
  style,
  schemaDefaultAction,
  isNewItem = false,
}) => {
  const [title, value, type, label] = chosenOption;
  const isPdfForm = isPdfFormType(editor.formType);
  const isAcknowledgment = schemaDefaultAction?.name === NotaryActionName.ACKNOWLEDGMENT;
  const hasSavedDocumentTitle = (action?.args?.length ?? 0) > 1;
  const isBackgroundColorEnabled = useFeatureToggle(FeatureFlag.EnableDynamicFieldBackgroundColor);

  const initialActor = action?.args?.[0] || NotaryActor.ALL_BORROWERS;
  const initialActorOption =
    findOptionByValue(notaryActorOptions, initialActor) || notaryActorOptions[0];

  const { form, setSchema, setInForm, validateField, handleChange } = useForm({
    actor: initialActorOption,
    documentTitle: action?.args?.[1] || '',
    isBold: !!style?.isBold,
    isUppercase: !!style?.isUppercase,
    horizontalAlignment: isPdfForm
      ? getInitialHorizontalAlignment({ align: style?.align, isNewItem })
      : null,
    fontColor: style?.fontColor,
    backgroundColor: isPdfForm ? style?.backgroundColor || null : null,
    fontSize: { value: style?.fontSize },
    fontFamily: { value: style?.fontFamily },
  });

  const onAutocompleteChange = useMemo(
    () => onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      const actionName = action?.name || NotaryActionName.ACKNOWLEDGMENT;
      const selectedActor = form.actor?.value || NotaryActor.ALL_BORROWERS;
      const args =
        isAcknowledgment || hasSavedDocumentTitle
          ? [selectedActor, form.documentTitle?.trim() || '']
          : [selectedActor];

      onSave({
        title,
        value,
        type,
        label,
        action: {
          name: actionName,
          args,
        },
        style: {
          isBold: form.isBold,
          isUppercase: form.isUppercase,
          align: form.horizontalAlignment?.value,
          fontSize: form.fontSize?.value,
          fontColor: form.fontColor,
          fontFamily: form.fontFamily?.value,
          ...getConditionalObject(isPdfForm, { backgroundColor: form.backgroundColor || null }),
        },
      });
    },
    [
      onSave,
      title,
      value,
      type,
      label,
      form,
      action,
      isPdfForm,
      isAcknowledgment,
      hasSavedDocumentTitle,
    ],
  );

  useEffect(() => {
    setSchema(notaryOptionsSchema);
  }, [setSchema]);

  return (
    <DynamicFieldOptionsLayout
      chosenOption={chosenOption}
      dataTestId={formsTestIds.DYNAMIC_FIELD_NOTARY_OPTIONS_VIEW}
      editor={editor}
      setParentView={setParentView}
      onClose={handleClose}
      onSubmit={onSubmit}
    >
      <Box sx={fullWidthCellStyles}>
        <Typography color="text.secondary" sx={sectionHeaderStyles} variant="bold12">
          Notary Options
        </Typography>
      </Box>

      <AutocompleteCDS
        disableClearable
        data-testid={formsTestIds.NOTARY_OPTIONS_ACTOR_SELECT}
        label="Actor"
        name="actor"
        options={notaryActorOptions}
        value={form.actor}
        onChange={onAutocompleteChange}
      />

      <Box />

      <TextFieldCDS
        customStyles={fullWidthCellStyles}
        dataTestId={formsTestIds.NOTARY_OPTIONS_DOCUMENT_TITLE_INPUT}
        isHidden={!isAcknowledgment}
        label="Document title"
        name="documentTitle"
        placeholder="e.g. Deed of Trust"
        value={form.documentTitle}
        onChange={handleChange}
      />

      <DynamicFieldBoldCheckbox value={form.isBold} onChange={handleChange} />

      <Box />

      <StyleViewOptions
        backgroundColor={form.backgroundColor}
        fontColor={form.fontColor}
        fontFamily={form.fontFamily}
        fontSize={form.fontSize}
        handleChange={handleChange}
        horizontalAlignment={form.horizontalAlignment}
        isBackgroundColorEnabled={isPdfForm && isBackgroundColorEnabled}
        isBold={form.isBold}
        setInForm={setInForm}
        validateField={validateField}
      />
    </DynamicFieldOptionsLayout>
  );
};

DynamicFieldNotaryOptionsView.propTypes = {
  chosenOption: PropTypes.array.isRequired,
  editor: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  setParentView: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  action: PropTypes.object,
  style: PropTypes.object,
  isNewItem: PropTypes.bool,
  schemaDefaultAction: PropTypes.shape({
    name: PropTypes.string,
  }),
};

export default DynamicFieldNotaryOptionsView;
